import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const GoalCard = ({ goal, onUpdate }) => {
  const navigate = useNavigate();
  const [currentValue, setCurrentValue] = useState(() => {
    if (!goal.current_value) return '0,00';
    return parseFloat(goal.current_value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isConcluding, setIsConcluding] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  // Formatar data sem problemas de timezone
  const formatDate = (date) => {
    if (!date) return '';
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const [year, month] = dateStr.split('T')[0].split('-');
    const months = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
    return `${months[parseInt(month, 10) - 1]} de ${year.slice(2)}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0,00';
    return parseFloat(num).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Converter formato brasileiro para decimal
  const parseBrazilianNumber = (str) => {
    if (!str) return 0;
    const cleaned = String(str).replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const handleQuickAdd = (percentage) => {
    const target = parseFloat(goal.target_value || 0);
    const current = parseBrazilianNumber(currentValue);
    const addValue = (target * percentage) / 100;
    const newValue = current + addValue;
    setCurrentValue(formatNumber(newValue));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (goal.status === 'concluded') return;

    setIsUpdating(true);
    setUpdateMessage(null);

    try {
      const valueDecimal = parseBrazilianNumber(currentValue);
      
      const response = await axios.post(`/goals/${goal.id}/progress`, {
        current_value_decimal: valueDecimal
      });

      if (response.data.success) {
        setUpdateMessage({ type: 'success', text: response.data.message });
        
        // Atualizar o card se houver callback
        if (onUpdate) {
          onUpdate({
            ...goal,
            current_value: response.data.current_value,
            progress_percentage: response.data.progress_percentage,
            status: response.data.status
          });
        }

        // Limpar mensagem após 2 segundos
        setTimeout(() => setUpdateMessage(null), 2000);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      setUpdateMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Erro ao atualizar progresso' 
      });
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setIsUpdating(false);
    }
  };

  const getPeriodIcon = (period) => {
    const icons = {
      daily: 'fa-calendar-day',
      weekly: 'fa-calendar-week',
      monthly: 'fa-calendar-alt',
      quarterly: 'fa-calendar',
      annual: 'fa-calendar-check'
    };
    return icons[period] || 'fa-calendar';
  };

  const handleConclude = async () => {
    if (isConcluding || goal.status === 'concluded') return;
    
    // Confirmação antes de concluir
    if (!window.confirm('Deseja concluir esta meta?')) return;

    setIsConcluding(true);
    setUpdateMessage(null);

    try {
      const response = await axios.post(`/goals/${goal.id}/toggle-status`, {
        action: 'conclude'
      });

      if (response.data.success) {
        setUpdateMessage({ type: 'success', text: 'Meta concluída!' });
        
        // Atualizar o card se houver callback
        if (onUpdate) {
          onUpdate({
            ...goal,
            status: 'concluded'
          });
        }

        // Se auto-repeat criou uma nova meta, mostrar informação
        if (response.data.next_goal) {
          setTimeout(() => {
            setUpdateMessage({ 
              type: 'success', 
              text: `Meta concluída! Nova meta criada para o próximo período.` 
            });
          }, 500);
        }

        setTimeout(() => setUpdateMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error concluding goal:', error);
      setUpdateMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Erro ao concluir meta' 
      });
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setIsConcluding(false);
    }
  };

  const getPeriodLabel = (period) => {
    const labels = {
      daily: 'diário',
      weekly: 'semanal',
      monthly: 'mensal',
      quarterly: 'trimestral',
      annual: 'anual',
    };
    return labels[period] || period;
  };

  const handleSkip = async () => {
    if (isSkipping || goal.status !== 'active') return;

    const periodLabel = getPeriodLabel(goal.period);
    if (!window.confirm(`Deseja pular este período (${periodLabel})? A meta será marcada como pulada e uma nova será criada para o próximo período.`)) return;

    setIsSkipping(true);
    setUpdateMessage(null);

    try {
      const response = await axios.post(`/goals/${goal.id}/toggle-status`, {
        action: 'skip'
      });

      if (response.data.success) {
        setUpdateMessage({ type: 'success', text: 'Período pulado!' });

        if (onUpdate) {
          onUpdate({ ...goal, status: 'skipped' });
        }

        // Se criou nova meta, navegar para ela
        if (response.data.next_goal && response.data.next_goal.id) {
          setTimeout(() => {
            navigate(`/goals/${response.data.next_goal.id}`);
          }, 500);
        }

        setTimeout(() => setUpdateMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error skipping goal:', error);
      setUpdateMessage({
        type: 'error',
        text: error.response?.data?.error || 'Erro ao pular meta'
      });
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setIsSkipping(false);
    }
  };

  const handleDeactivate = async () => {
    if (isDeactivating) return;

    if (!window.confirm('Deseja inativar esta meta e todas as metas relacionadas?')) return;

    setIsDeactivating(true);
    setUpdateMessage(null);

    try {
      const response = await axios.post(`/goals/${goal.id}/toggle-status`, {
        action: 'deactivate'
      });

      if (response.data.success) {
        setUpdateMessage({ type: 'success', text: 'Meta inativada!' });

        if (onUpdate) {
          onUpdate({ ...goal, status: 'inactive' });
        }

        setTimeout(() => setUpdateMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error deactivating goal:', error);
      setUpdateMessage({
        type: 'error',
        text: error.response?.data?.error || 'Erro ao inativar meta'
      });
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setIsDeactivating(false);
    }
  };

  // Calcular progresso baseado nos valores (fallback se progress_percentage não vier)
  const calculateProgress = () => {
    // Se já temos progress_percentage válido, usar ele
    if (goal.progress_percentage && parseFloat(goal.progress_percentage) > 0) {
      return parseFloat(goal.progress_percentage);
    }
    // Calcular baseado em current_value / target_value
    const current = parseFloat(goal.current_value) || 0;
    const target = parseFloat(goal.target_value) || 0;
    if (target === 0) return 0;
    return (current / target) * 100;
  };

  // Calcular se a meta foi alcançada ou não
  const progressValue = calculateProgress();
  const isPositiveGoal = goal.is_positive_goal !== false; // default true
  
  // Meta alcançada com sucesso:
  // - Positiva: progresso >= 100% (quanto mais, melhor)
  // - Negativa: progresso <= 100% (quanto menos, melhor - não ultrapassou o limite)
  const isGoalAchieved = isPositiveGoal 
    ? progressValue >= 100 
    : progressValue <= 100;
  
  // Meta falhada (só mostra quando concluída)
  const isGoalFailed = goal.status === 'concluded' && !isGoalAchieved;

  // Determinar classe do card
  const cardClass = goal.status === 'concluded' 
    ? (isGoalFailed ? 'failed' : 'concluded') 
    : '';

  return (
    <div className={`goals-card goals-card-compact ${cardClass}`} data-repeat-type={goal.repeat_type}>
      {/* Tag de status (visível quando status = concluded) */}
      {goal.status === 'concluded' && (
        isGoalFailed ? (
          <div className="goals-failed-tag">
            <i className="fas fa-times-circle"></i>
            <span>NÃO ALCANÇADA</span>
          </div>
        ) : (
          <div className="goals-concluded-tag">
            <i className="fas fa-check-circle"></i>
            <span>CONCLUÍDA</span>
          </div>
        )
      )}
      
      {/* Header compacto: título + porcentagem */}
      <div className="goals-card-header-compact">
        <div className="goals-header-row">
          <Link to={`/goals/${goal.latest_goal_id || goal.id}?detail=true`} className="goals-card-title-link">
            <h3 className="goals-card-title-compact">{goal.title}</h3>
          </Link>
          <div className={`goals-progress-percentage-compact ${goal.is_positive_goal === false ? 'negative-goal' : ''} ${progressValue >= 100 ? 'exceeded' : ''}`}>
            {progressValue.toFixed(0)}%
            {/* Troféu para metas positivas que excedem, aviso para negativas que excedem */}
            {progressValue >= 100 && (
              goal.is_positive_goal === false 
                ? <i className="fas fa-exclamation-triangle ms-1" title="Limite excedido"></i>
                : <i className="fas fa-trophy ms-1" title="Meta alcançada"></i>
            )}
          </div>
        </div>
        
        {/* Badges em linha única */}
        <div className="goals-badges-compact">
          <span className={`goals-badge-mini ${goal.period}`} title={getPeriodLabel(goal.period)}>
            <i className={`fas ${getPeriodIcon(goal.period)}`}></i>
            <span className="goals-badge-mini-label">{getPeriodLabel(goal.period)}</span>
          </span>
          {goal.start_date && (
            <span className="goals-badge-mini date" title={`${formatDate(goal.start_date)}${goal.end_date ? ' - ' + formatDate(goal.end_date) : ''}`}>
              <i className="fas fa-calendar-alt"></i>
              <span className="goals-badge-mini-label">
                {formatDateShort(goal.start_date)}
              </span>
            </span>
          )}
          {goal.status !== 'concluded' && (
            <span className={`goals-badge-mini status-${goal.status}`}>
              {goal.status === 'active' && <i className="fas fa-play"></i>}
              {goal.status === 'inactive' && <i className="fas fa-pause"></i>}
            </span>
          )}
          {goal.department_name && (
            <span className="goals-badge-mini dept" title={goal.department_name}>
              <i className="fas fa-building"></i>
            </span>
          )}
        </div>
      </div>

      {/* Barra de progresso compacta */}
      <div className="goals-progress-compact-bar">
        <div className="goals-progress-bar-mini">
          <div 
            className={`goals-progress-fill ${progressValue >= 100 ? 'exceeded' : ''} ${goal.is_positive_goal === false ? 'negative-goal' : ''}`}
            style={{ width: `${Math.min(progressValue, 100)}%` }}
          ></div>
        </div>
        <div className="goals-values-compact">
          <span>{formatNumber(goal.current_value)}</span>
          <span className="goals-separator">/</span>
          <span>{formatNumber(goal.target_value)}</span>
        </div>
      </div>

      {/* Form de atualização compacto */}
      {goal.status !== 'concluded' && (
        <div className="goals-update-compact">
          {updateMessage && (
            <div className={`goals-msg-compact ${updateMessage.type}`}>
              {updateMessage.text}
            </div>
          )}
          <form className="goals-form-compact" onSubmit={handleSubmit}>
            <input 
              type="text" 
              className={`goals-input-mini ${isUpdating ? 'loading' : ''}`}
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
              placeholder="0,00"
              disabled={isUpdating}
            />
            <div className="goals-quick-btns">
              <button type="button" className="goals-btn-mini" disabled={isUpdating} onClick={() => handleQuickAdd(10)}>+10%</button>
              <button type="button" className="goals-btn-mini" disabled={isUpdating} onClick={() => handleQuickAdd(25)}>+25%</button>
            </div>
            <button type="submit" className="goals-btn-save" disabled={isUpdating}>
              <i className={`fas ${isUpdating ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
            </button>
          </form>
        </div>
      )}

      {/* Ações compactas */}
      <div className="goals-actions-compact">
        <Link to={`/goals/${goal.latest_goal_id || goal.id}?detail=true`} className="goals-action-btn info" title="Detalhes">
          <i className="fas fa-chart-line"></i>
        </Link>
        <Link to={`/goals/${goal.latest_goal_id || goal.id}/edit`} className="goals-action-btn edit" title="Editar">
          <i className="fas fa-pencil-alt"></i>
        </Link>
        <Link to={`/goals/${goal.latest_goal_id || goal.id}/targets`} className="goals-action-btn target" title="Metas">
          <i className="fas fa-bullseye"></i>
        </Link>
        {goal.status === 'active' && (
          <>
            <button 
              className={`goals-action-btn conclude ${isConcluding ? 'loading' : ''}`} 
              title="Concluir"
              onClick={handleConclude}
              disabled={isConcluding}
            >
              <i className={`fas ${isConcluding ? 'fa-spinner fa-spin' : 'fa-check-circle'}`}></i>
            </button>
            <button 
              className={`goals-action-btn skip ${isSkipping ? 'loading' : ''}`} 
              title="Pular período"
              onClick={handleSkip}
              disabled={isSkipping}
            >
              <i className={`fas ${isSkipping ? 'fa-spinner fa-spin' : 'fa-forward'}`}></i>
            </button>
            <button 
              className={`goals-action-btn deactivate ${isDeactivating ? 'loading' : ''}`} 
              title="Inativar"
              onClick={handleDeactivate}
              disabled={isDeactivating}
            >
              <i className={`fas ${isDeactivating ? 'fa-spinner fa-spin' : 'fa-pause-circle'}`}></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoalCard;
